import React from "react";

import { useState, useMemo, useEffect } from "react";

import "../Grids.css";
import {
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaSort,
  FaPlusCircle,
  FaFilePdf,
  FaCalendarAlt,
  FaStickyNote,
} from "react-icons/fa";

import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Switch } from "@progress/kendo-react-inputs";

import { dropDown, invoiceGrid } from "../../../data/interfaces";
import moment from "moment";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    numberRange: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const breakpoint = 1800;

//#region Fuzzy filter
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// Simple numberRange filter - not used in this grid but required by the interface
const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  return true; // Always return true since we don't use number range filtering in this grid
};
//#endregion

function InvoiceGrid(props: any) {
   //#region Hide columns on window resize
   const [windowWidth, setWindowWidth] = useState(window.innerWidth);
   const [isVisible, setIsVisible] = useState(true);

   const [showCompleted, setShowCompleted] = useState(false);
   const [showDeleted, setShowDeleted] = useState(false);

   const [monthSelect, setMonthSelect] = useState<dropDown[]>([
      {value: 0, text: "" },
      {value: 1, text: "January" },
      {value: 2, text: "February" },
      {value: 3, text: "March" },
      {value: 4, text: "April" },
      {value: 5, text: "May" },
      {value: 6, text: "June" },
      {value: 7, text: "July" },
      {value: 8, text: "August" },
      {value: 9, text: "September" },
      {value: 10, text: "October" },
      {value: 11, text: "November" },
      {value: 12, text: "December" },
    ])

   
   const handleShowCompleted = (event: any) => {
      setShowCompleted(event.target.value);
      props.onShowCompleted(event.target.value);
   };

   const handleShowDeleted = (event: any) => {
      setShowDeleted(event.target.value);
      props.onShowDeleted(event.target.value);
   };

   const toggleVisibility = (value: any) => {
      setIsVisible(typeof value === "boolean" ? value : !isVisible);
   };

   useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);

      return () => {
         window.removeEventListener("resize", handleResize);
      };
   }, []);

   useEffect(() => {
    toggleVisibility(windowWidth >= breakpoint);
  }, [windowWidth]);
  //#endregion

   const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
   const [globalFilter, setGlobalFilter] = useState("");
   const [data, setData] = useState<invoiceGrid[]>([]);

   useEffect(() => {
      setData(props.onData);
   }, [props.onData]);

   //#region Column definitions
   const clientColumns = useMemo(() => {
      const columns: ColumnDef<invoiceGrid>[] = [
      {
         accessorKey: "refFormatted",
         header: "Invoice No",
         footer: (props: { column: { id: any } }) => props.column.id,
         cell: ({ row }: { row: { original: invoiceGrid } }) => {
            const invoiceId = row.original.invoiceId;

            const invoiceGenHandler = () => {
               props.onInvoiceExport(invoiceId, row.original.refFormatted);
            };

            return (
               <div style={{ display: "flex", flexDirection: "row" }}>
                  <Tooltip anchorElement="target" position="top">
                     <FaFilePdf
                        title="Generate Invoice"
                        style={{
                        fontSize: "14pt",
                        marginRight: "1em",
                        cursor: "pointer",
                        color: "rgb(94 142 23)",
                        }}
                        onClick={invoiceGenHandler}
                     />
                  </Tooltip>
                  <span
                     style={{
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                     }}
                  >
                     {row.original.refFormatted}
                     {
                        row.original.noteCount > 0 ? (
                           <>
                           {" "}<FaStickyNote />
                           </>
                        ): null
                     }
                  </span>
               </div>
            );
         },
      },      
      {
         accessorKey: "client",
         header: "Client",
         footer: (props: { column: { id: any } }) => props.column.id,
      },
      {
         accessorKey: "site",
         header: "Site",
         footer: (props: { column: { id: any } }) => props.column.id,
         cell: ({ row }: { row: { original: invoiceGrid } }) => (
            <Tooltip anchorElement="target" position="top">
               <span title={row.original.site} className="overflow__container">
                  {row.original.site}
               </span>
            </Tooltip>
         ),
      },      
      {
         accessorKey: "invoiceType",
         header: "Type of Invoice",
         footer: (props: { column: { id: any } }) => props.column.id,
      },
      {
         accessorKey: "value",
         header: "Invoice Value",
         enableColumnFilter: false,
         footer: (props: { column: { id: any } }) => props.column.id,
         cell: ({ row }: { row: { original: invoiceGrid } }) => (
            <div style={{ fontWeight: "bold", display: "flex", flexDirection: "row", alignItems: "center" }}>
               <span
                  style={{
                     height: "6px",
                     width: "6px",
                     backgroundColor: row.original.value <= 0 ? "#ee7a7a" : "rgb(129 192 35)",
                     borderRadius: "50%",
                     display: "inline-block",
                     marginRight: "8px",
                  }}
               ></span>
               £{row.original.value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          ),
      },
      {
         accessorKey: "invoiceDate",
         header: "Invoice Date",
         footer: (props: { column: { id: any } }) => props.column.id,
         cell: ({ row }: { row: { original: invoiceGrid } }) => (
            <div>{row.original.invoiceDate ? moment(row.original.invoiceDate).format("DD/MM/YYYY") : "No invoice date"}</div>
          ),
      },
      {
         accessorKey: "emailedDate",
         header: "Emailed",
         footer: (props: { column: { id: any } }) => props.column.id,
         cell: ({ row }: { row: { original: invoiceGrid } }) => (
            <div>{row.original.emailedDate ? moment(row.original.emailedDate).format("DD/MM/YYYY HH:mm") : ""}</div>
          ),
      },
      {
         accessorKey: "createdBy",
         header: "Created By",
         footer: (props: { column: { id: any } }) => props.column.id,
      },      
      ].filter(Boolean) as ColumnDef<invoiceGrid>[];

      return columns;
   }, [isVisible]);
   //#endregion

   const table = useReactTable({
      data,
      columns: clientColumns,
      filterFns: {
         fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
      },
      state: {
         columnFilters,
         globalFilter,
      },
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      globalFilterFn: fuzzyFilter,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      getFacetedMinMaxValues: getFacetedMinMaxValues(),
      debugTable: false,
      debugHeaders: false,
      debugColumns: false,
   });

   const handleMonthCreated = (event: any) => {
      props.onMonthCreatedBy(+event.target.value);
   }

   return (
      <div className="p-2">
         {/* This is the global column search box */}
         <div className="grid__title__container">
            <div className="grid__header__background">
               <div className="grid__title">
                  <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }}>
                     <div>Sales</div>
                     <select
                        style={{marginLeft: 10, height: 40, width: 200}}
                        className="k-dropdownlist"
                        onChange={handleMonthCreated}
                     >
                        {monthSelect.map((month, idx) => <option key={idx} value={month.value}>{month.text}</option> )}
                     </select>
                     <Tooltip anchorElement="target" position="top" openDelay={0}>
                        <FaCalendarAlt
                           style={{
                           fontSize: "14pt",
                           marginLeft: "0.7rem",                    
                           }}
                           title="Invoice created in"
                        />
                     </Tooltip>
                  </div>
                  {
                     props.lockEdit === false ? (
                     <div style={{ display: "flex", flexDirection: "row" }}>
                        <div onClick={() => props.onAddToggle()} className="grid__addrecord__button">
                           <FaPlusCircle
                              style={{ fontSize: "14pt", marginRight: "1rem" }}
                           />{" "}
                           <p style={{ margin: 0 }}>Add new invoice</p>
                        </div>
                     </div>
                     ) : null
                  }
               </div>
               <DebouncedInput
                  value={globalFilter ?? ""}
                  onChange={(value) => setGlobalFilter(String(value))}
                  placeholder="Search all invoice columns..."
                  className="filter__input__global"
               />
            </div>
         </div>

         <table className="grid__table">
         <thead>
            {table.getHeaderGroups().map((headerGroup) => (
               <tr key={headerGroup.id}>
               {headerGroup.headers.map((header) => {
                  return (
                     <th key={header.id} colSpan={header.colSpan}>
                     {header.isPlaceholder ? null : (
                        <>
                           {/* This DIV is where you hover/click on the column header to sort */}
                           <div
                           {...{
                              className: header.column.getCanSort()
                                 ? "header__sortable"
                                 : "",
                              onClick: header.column.getToggleSortingHandler(),
                           }}
                           >
                           {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                           )}
                           {{
                              asc: (
                                 <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                              ),
                              desc: (
                                 <FaSortAmountDown style={{ fontSize: "14pt" }} />
                              ),
                           }[header.column.getIsSorted() as string] ?? (
                              <FaSort style={{ fontSize: "14pt" }} />
                           )}
                           </div>

                           {header.column.getCanFilter() ? (
                           <div>
                              <Filter column={header.column} table={table} />
                           </div>
                           ) : null}
                        </>
                     )}
                     </th>
                  );
               })}
               </tr>
            ))}
         </thead>
         <tbody>
            {table.getRowModel().rows.map((row) => {
               const bgColor = () => {
               if (row.original.deletedDate) {
                  return "rgba(235, 199, 199, 0.5)";
               } else if (row.original.completedDate) {
                  return "#d7edd7";
               } else {
                  return "rgb(245 249 251)";
               }
               };

               return (
               <tr
                  onDoubleClick={() => {
                     props.onEditToggle(row.original.invoiceId);
                  }}
                  key={row.id}
                  style={{
                     backgroundColor: bgColor(),
                     border: "1px solid rgb(216 229 237)",
                  }}
               >
                  {row.getVisibleCells().map((cell) => {
                     const isDeleted = cell.row.original.deletedDate;

                     const textColor = isDeleted ? "rgb(137 5 5)" : "#1e2a4e";
                     return (
                     <td style={{ color: textColor }} key={cell.id}>
                        {flexRender(
                           cell.column.columnDef.cell,
                           cell.getContext()
                        )}
                     </td>
                     );
                  })}
               </tr>
               );
            })}
         </tbody>
         </table>

         {/* This is the pagination controls */}
         <div className="page__container">
         <div className="page__button__container">
            <button
               className="page__button"
               onClick={() => table.setPageIndex(0)}
               disabled={!table.getCanPreviousPage()}
            >
               {"<<"}
            </button>
            <button
               className="page__button"
               onClick={() => table.previousPage()}
               disabled={!table.getCanPreviousPage()}
            >
               {"<"}
            </button>
            <button
               className="page__button"
               onClick={() => table.nextPage()}
               disabled={!table.getCanNextPage()}
            >
               {">"}
            </button>
            <button
               className="page__button"
               onClick={() => table.setPageIndex(table.getPageCount() - 1)}
               disabled={!table.getCanNextPage()}
            >
               {">>"}
            </button>
         </div>

         <div className="page__controls">
            <div className="page__numbers">
               <div>Page&nbsp;</div>
               <strong>
               {table.getState().pagination.pageIndex + 1} of{" "}
               {table.getPageCount()}
               </strong>
            </div>
            <div className="page__numbers__goto">
               <div className="page__numbers__gototext">| Go to page:</div>
               <input
               type="number"
               defaultValue={table.getState().pagination.pageIndex + 1}
               onChange={(e) => {
                  const page = e.target.value ? Number(e.target.value) - 1 : 0;
                  table.setPageIndex(page);
               }}
               className="filter__input"
               />
            </div>
            <select
               value={table.getState().pagination.pageSize}
               onChange={(e) => {
               table.setPageSize(Number(e.target.value));
               }}
               className="page__select"
            >
               {[10, 20, 30, 40, 50].map((pageSize) => (
               <option key={pageSize} value={pageSize}>
                  Show {pageSize}
               </option>
               ))}
            </select>
         </div>
         </div>
      </div>
   );
}

//#region Filter component
function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}
//#endregion

//#region A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}
//#endregion

export default InvoiceGrid;
